import React, { useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Chip, TextField, InputAdornment, Menu, MenuItem, Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { C, bandColor, bandLabel, fmtFull, exportCSV, exportExcel, exportPDF } from "./reportingUtils";

const COLS = [
  { key: "name", label: "Company", w: "2fr", type: "text" },
  { key: "score", label: "ARC", w: "0.6fr", type: "num" },
  { key: "recLimit", label: "Rec. limit", w: "1fr", type: "money" },
  { key: "appLimit", label: "Approved", w: "1fr", type: "money" },
  { key: "sector", label: "Sector", w: "1.2fr", type: "text" },
  { key: "country", label: "Country", w: "1fr", type: "text" },
  { key: "status", label: "Status", w: "0.9fr", type: "text" },
];

// FR-10 / FR-11: clicking a KPI or chart element opens the underlying record list, which
// supports search, sorting and export.
const DrillDownDialog = ({ open, title, rows, onClose, onOpenCompany }) => {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("recLimit");
  const [sortDir, setSortDir] = useState("desc");
  const [exportEl, setExportEl] = useState(null);

  const view = useMemo(() => {
    const term = q.trim().toLowerCase();
    let r = rows || [];
    if (term) r = r.filter((x) => `${x.name} ${x.sector} ${x.country} ${x.status}`.toLowerCase().includes(term));
    const dir = sortDir === "asc" ? 1 : -1;
    return [...r].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === "number" || typeof bv === "number") return ((av || 0) - (bv || 0)) * dir;
      return String(av || "").localeCompare(String(bv || "")) * dir;
    });
  }, [rows, q, sortKey, sortDir]);

  const setSort = (k) => { if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir(k === "name" || k === "sector" || k === "country" || k === "status" ? "asc" : "desc"); } };

  const exportHeaders = COLS.map((c) => ({ key: c.key, label: c.label }));
  const exportRows = view.map((r) => ({ ...r, score: r.score ?? "", recLimit: Math.round(r.recLimit || 0), appLimit: Math.round(r.appLimit || 0) }));
  const fname = `CrediArc-${(title || "records").replace(/[^a-z0-9]+/gi, "-")}`;
  const doExport = (fmt) => {
    setExportEl(null);
    if (fmt === "csv") exportCSV(exportRows, exportHeaders, fname);
    else if (fmt === "xlsx") exportExcel(exportRows, exportHeaders, fname, "Records");
    else exportPDF(exportRows, exportHeaders, fname, title, `${view.length} counterparties`);
  };

  const grid = COLS.map((c) => c.w).join(" ");
  const cell = (r, c) => {
    if (c.key === "name") return <Typography sx={{ fontSize: 12.5, color: C.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</Typography>;
    if (c.key === "score") return r.score == null ? <span style={{ color: C.muted }}>—</span> : <Chip label={r.score.toFixed(1)} size="small" sx={{ height: 19, fontWeight: 700, fontSize: 11, background: `${bandColor(r.score)}1A`, color: bandColor(r.score) }} />;
    if (c.type === "money") return <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtFull(r[c.key])}</span>;
    return <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r[c.key] || "—"}</span>;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: "14px", maxHeight: "86vh" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, pb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink }}>{title}</Typography>
          <Typography sx={{ fontSize: 12.5, color: C.muted }}>{view.length} counterparties</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button size="small" startIcon={<DownloadIcon />} onClick={(e) => setExportEl(e.currentTarget)} sx={{ textTransform: "none", color: C.ink }}>Export</Button>
          <Menu anchorEl={exportEl} open={!!exportEl} onClose={() => setExportEl(null)}>
            <MenuItem onClick={() => doExport("csv")}>CSV</MenuItem>
            <MenuItem onClick={() => doExport("xlsx")}>Excel</MenuItem>
            <MenuItem onClick={() => doExport("pdf")}>PDF</MenuItem>
          </Menu>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company, sector, country…" size="small" fullWidth sx={{ mb: 1.5 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.muted }} /></InputAdornment> }} />
        <Box sx={{ border: `1px solid ${C.line}`, borderRadius: "10px", overflow: "hidden" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: grid, background: C.ground, px: 1.25, py: 0.75, position: "sticky", top: 0 }}>
            {COLS.map((c) => (
              <Box key={c.key} onClick={() => setSort(c.key)} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", userSelect: "none" }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: sortKey === c.key ? C.blue : C.muted }}>{c.label}</Typography>
                {sortKey === c.key && (sortDir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 13, color: C.blue }} /> : <ArrowDownwardIcon sx={{ fontSize: 13, color: C.blue }} />)}
              </Box>
            ))}
          </Box>
          <Box sx={{ maxHeight: "52vh", overflowY: "auto" }}>
            {view.length === 0 && <Typography sx={{ fontSize: 13, color: C.muted, textAlign: "center", py: 4 }}>No matching counterparties.</Typography>}
            {view.map((r, i) => {
              const clickable = onOpenCompany && r.companyId;
              return (
                <Box key={i} onClick={clickable ? () => onOpenCompany(r.companyId) : undefined}
                  sx={{ display: "grid", gridTemplateColumns: grid, px: 1.25, py: 0.9, borderTop: `1px solid ${C.line}`, alignItems: "center", fontSize: 12.5, color: C.slate, cursor: clickable ? "pointer" : "default", "&:hover": clickable ? { background: "#F7F9FE" } : {} }}>
                  {COLS.map((c) => <Box key={c.key} sx={{ minWidth: 0, pr: 1 }}>{cell(r, c)}</Box>)}
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DrillDownDialog;
